import {
  aesGcmDecryptBuffer,
  aesGcmEncryptBuffer,
  generateDek,
  unwrapDek,
  wrapDek,
} from '@docuid/crypto-client';

import type { DocuIdDocument, Document } from '../../common/types';
import { formatFileSize, getFileExtension } from '../../common/utils';
import { addinClient } from '../addinClient';
import { AuthService } from '../AuthService';
import { DocuIdApiService } from '../DocuIdApiService';
import { logger } from '../Logger';
import { OfficeHostService } from '../OfficeHostService';
import { clearExcelDocument, insertIntoExcel } from './ExcelService';
import { getActiveDocumentBytes } from './officeFileHelper';
import { clearPowerPointDocument, insertIntoPowerPoint } from './PowerPointService';
import type { DocumentContent } from './types';
import { clearWordDocument, insertIntoWord } from './WordService';

const documentLogger = logger.createContextLogger('DocumentService');

export const DocumentService = {
  async getDocuments(): Promise<Document[]> {
    try {
      const host = OfficeHostService.getHost();
      documentLogger.info(`Fetching ${host} documents from DocuID API`);

      const apiDocs = await DocuIdApiService.getDocuments(host);

      if (AuthService.isDemoMode()) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }

      const documents: Document[] = apiDocs.map(
        (document: DocuIdDocument & { _uuid?: string }) => ({
          // Use the UUID as the document ID for all oRPC calls
          id: document._uuid ?? document.documentId.toString(),
          title: document.fileName,
          type: getFileExtension(document.fileName),
          dateModified: new Date(document.updatedAt).toLocaleDateString(),
          size: formatFileSize(document.fileSize),
          documentId: document.documentId,
          isPasswordProtected: document.isPasswordProtected,
          description: document.description,
        }),
      );

      documentLogger.info('Documents fetched successfully', { count: documents.length });
      return documents;
    } catch (error) {
      documentLogger.error('Failed to fetch documents', error as Error);
      throw error;
    }
  },

  async openDocument(documentId: string): Promise<void> {
    try {
      documentLogger.info(`Opening document: ${documentId}`);

      // documentId here is now a UUID string from the new API
      const accessInfo = await DocuIdApiService.getDocumentAccess(documentId);

      if (!accessInfo.access.url) {
        throw new Error('Document access URL not available');
      }

      // 1. Download raw ciphertext (encrypted bytes) from server
      let blobContent = await DocuIdApiService.downloadDocumentContent(accessInfo.access.url);

      // 2. Decrypt in-browser if file is marked as client-side encrypted
      if (accessInfo.isEncrypted) {
        documentLogger.info('Document is encrypted. Starting client-side decryption...', {
          documentId,
        });

        // Retrieve decrypted user private key from memory cache
        const privateKey = AuthService.getUserPrivateKey();
        if (!privateKey) {
          throw new Error(
            'Session locked or key unavailable. Please re-authenticate via biometric check.',
          );
        }

        // Fetch wrapped document key from backend
        documentLogger.info('Fetching wrapped document encryption key (DEK)...');
        const documentKey = await addinClient.encryptionKeys.getDocumentKey({ documentId });

        if (documentKey.model === 'client_rsa') {
          documentLogger.info('Unwrapping DEK and decrypting content locally...');
          // Decrypt the Document Encryption Key
          const dek = await unwrapDek(documentKey.wrappedDek, privateKey);

          // Read blob as ArrayBuffer for decryption
          const ciphertext = await blobContent.arrayBuffer();

          // Decrypt ciphertext using AES-GCM
          const plaintext = await aesGcmDecryptBuffer(ciphertext, dek, documentKey.fileIv);

          // Construct plain document blob
          blobContent = new Blob([new Uint8Array(plaintext)], { type: accessInfo.fileType });
          documentLogger.info('Decryption completed successfully');
        } else if (documentKey.model === 'none') {
          documentLogger.info('Document key model is unencrypted ("none"). Skipping decryption.');
        } else {
          throw new Error(`Unsupported encryption model: ${documentKey.model}`);
        }
      }

      const documentContent: DocumentContent = {
        id: documentId,
        content: '',
        contentType: accessInfo.fileType,
        fileName: accessInfo.fileName,
        documentType: getFileExtension(accessInfo.fileName),
        binaryContent: blobContent,
      };

      const host = OfficeHostService.getHost();

      switch (host) {
        case 'Excel': {
          await clearExcelDocument();
          await insertIntoExcel(documentContent);
          break;
        }
        case 'PowerPoint': {
          await clearPowerPointDocument();
          await insertIntoPowerPoint(documentContent);
          break;
        }
        default: {
          // Word or Unknown
          await clearWordDocument();
          await insertIntoWord(documentContent);
          break;
        }
      }

      documentLogger.info(`Successfully opened document: ${documentId}`, {
        fileName: documentContent.fileName,
        host,
      });
    } catch (error) {
      documentLogger.error(`Failed to open document: ${documentId}`, error as Error);
      throw error;
    }
  },

  async clearDocument(): Promise<void> {
    const host = OfficeHostService.getHost();
    documentLogger.info(`Clearing ${host} document content`);

    try {
      switch (host) {
        case 'Excel': {
          await clearExcelDocument();
          break;
        }
        case 'PowerPoint': {
          await clearPowerPointDocument();
          break;
        }
        default: {
          // Word or Unknown
          await clearWordDocument();
          break;
        }
      }
    } catch (error) {
      documentLogger.error(`Failed to clear ${host} document`, error as Error);
    }
  },

  async closeDocument(documentId: string): Promise<void> {
    documentLogger.info(`Closing document: ${documentId}`);
    await DocumentService.clearDocument();
  },

  /**
   * Save changes (new version) of the currently open document.
   */
  async saveEdits(documentId: string): Promise<void> {
    documentLogger.info('Saving edits to existing document', { documentId });
    if (AuthService.isDemoMode()) {
      documentLogger.info('Demo Mode active. Simulating save edits successfully.');
      await new Promise((resolve) => setTimeout(resolve, 600));
      return;
    }
    try {
      // 1. Get decrypted private key from memory
      const privateKey = AuthService.getUserPrivateKey();
      if (!privateKey) {
        throw new Error(
          'Session locked or key unavailable. Please log in again to unlock encryption keys.',
        );
      }

      // 2. Fetch document metadata and key
      const documentMeta = await addinClient.documents.get({ id: documentId });
      const documentKey = await addinClient.encryptionKeys.getDocumentKey({ documentId });

      if (documentKey.model !== 'client_rsa') {
        throw new Error(
          `Edits are only supported for client_rsa encrypted documents. Current model: ${documentKey.model}`,
        );
      }

      // 3. Unwrap the DEK using the user private key
      documentLogger.info('Unwrapping document encryption key...');
      const dek = await unwrapDek(documentKey.wrappedDek, privateKey);

      // 4. Retrieve active Office document bytes
      documentLogger.info('Reading active document bytes...');
      const fileBuffer = await getActiveDocumentBytes();

      // 5. Encrypt document bytes using the unwrapped DEK (AES-GCM generates a new IV)
      documentLogger.info('Encrypting document content...');
      const { ciphertext, iv } = await aesGcmEncryptBuffer(fileBuffer, dek);

      // 6. Create upload session for a new version (passes documentId)
      documentLogger.info('Creating upload session...');
      const session = await DocuIdApiService.createUploadSession({
        fileName: documentMeta.fileName,
        size: ciphertext.byteLength,
        mimeType: documentMeta.mimeType,
        documentId: documentId,
        encryption: {
          fileIv: iv,
          wrappedDek: documentKey.wrappedDek,
        },
      });

      // 7. Upload ciphertext bytes
      documentLogger.info('Uploading encrypted ciphertext...');
      await DocuIdApiService.uploadCiphertextBytes(
        session.uploadUrl,
        session.sessionId,
        ciphertext,
      );

      // 8. Complete session
      documentLogger.info('Finalizing version upload session...');
      await DocuIdApiService.completeUploadSession(session.sessionId);

      documentLogger.info('Document version successfully saved');
    } catch (error) {
      documentLogger.error('Failed to save document edits', error as Error);
      throw error;
    }
  },

  /**
   * Save the active document as a brand-new document in DocuID.
   */
  async saveAsNew(fileName: string, workspaceId?: string, folderId?: string): Promise<void> {
    documentLogger.info('Saving active document as new', { fileName, workspaceId, folderId });
    if (AuthService.isDemoMode()) {
      documentLogger.info('Demo Mode active. Simulating save as new successfully.');
      await new Promise((resolve) => setTimeout(resolve, 600));
      return;
    }
    try {
      // 1. Get user RSA public key PEM from memory
      const publicKeyPem = AuthService.getUserPublicKeyPem();
      if (!publicKeyPem) {
        throw new Error(
          'Session locked or key unavailable. Please log in again to unlock encryption keys.',
        );
      }

      const host = OfficeHostService.getHost();
      let mimeType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      let extension = '.docx';

      if (host === 'Excel') {
        mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        extension = '.xlsx';
      } else if (host === 'PowerPoint') {
        mimeType = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
        extension = '.pptx';
      }

      // Append extension if missing
      let finalName = fileName;
      if (!fileName.toLowerCase().endsWith(extension)) {
        finalName = fileName + extension;
      }

      // 2. Generate a new DEK locally
      documentLogger.info('Generating new document encryption key...');
      const dek = await generateDek();

      // 3. Wrap DEK with user's RSA public key
      documentLogger.info('Wrapping encryption key with user public key...');
      const wrappedDek = await wrapDek(dek, publicKeyPem);

      // 4. Retrieve active Office document bytes
      documentLogger.info('Reading active document bytes...');
      const fileBuffer = await getActiveDocumentBytes();

      // 5. Encrypt document bytes locally
      documentLogger.info('Encrypting document content...');
      const { ciphertext, iv } = await aesGcmEncryptBuffer(fileBuffer, dek);

      // 6. Create upload session for new document
      documentLogger.info('Creating upload session for new document...');
      const session = await DocuIdApiService.createUploadSession({
        fileName: finalName,
        size: ciphertext.byteLength,
        mimeType: mimeType,
        workspaceId: workspaceId || undefined,
        folderId: folderId || undefined,
        encryption: {
          fileIv: iv,
          wrappedDek: wrappedDek,
        },
      });

      // 7. Upload ciphertext bytes
      documentLogger.info('Uploading encrypted ciphertext...');
      await DocuIdApiService.uploadCiphertextBytes(
        session.uploadUrl,
        session.sessionId,
        ciphertext,
      );

      // 8. Complete session
      documentLogger.info('Finalizing new document upload session...');
      await DocuIdApiService.completeUploadSession(session.sessionId);

      documentLogger.info('New document successfully saved');
    } catch (error) {
      documentLogger.error('Failed to save as new document', error as Error);
      throw error;
    }
  },
};
